import { gql } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react'
import ClearIcon from '@mui/icons-material/Clear'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { endOfDay, startOfDay } from 'date-fns'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  type OrderStatus,
  OrdersDocument,
  type OrdersQuery,
  OrderType,
} from '../gql/graphql'
import { useSiteContext } from '../hooks'
import { formatMergedSlotName, mergeConsecutiveSlots } from '../lib/booking'
import OrderDialog from './OrderDialog'

gql`
  query Orders($filter: OrderFilterInput, $first: Int, $after: String) {
    orders(filter: $filter, first: $first, after: $after) {
      edges {
        node {
          id
          orderType
          status
          amount
          detail {
            courtName
            startTime
            price
          }
          user {
            id
            name
            mobile
            avatar
            openid
          }
          createTime
          payTime
          expiresAt
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

const statusLabels: Record<
  OrderStatus,
  { label: string; color: 'success' | 'warning' | 'default' | 'error' }
> = {
  PENDING: { label: '待支付', color: 'warning' },
  CONFIRMED: { label: '已支付', color: 'success' },
  CANCELLED: { label: '已取消', color: 'default' },
  CLOSED: { label: '已关闭', color: 'error' },
}

const orderTypeLabels: Record<OrderType, string> = {
  [OrderType.SlotBooking]: '订场',
  [OrderType.EventSignup]: '赛事',
  [OrderType.Coaching]: '培训',
  [OrderType.Membership]: '会员',
  [OrderType.Goods]: '商品',
}

function formatDetail(detail: OrderRow['detail']): string {
  if (!detail?.length) return '-'
  const merged = mergeConsecutiveSlots(
    detail.map((d) => ({
      courtId: d.courtName, // use courtName as grouping key since courtId is not in list query
      courtName: d.courtName,
      startTime: d.startTime,
      price: Number(d.price),
    })),
  )
  return merged.map(formatMergedSlotName).join('\n')
}

type OrderRow = OrdersQuery['orders']['edges'][number]['node']

const columns: GridColDef<OrderRow>[] = [
  {
    field: '__rowNumber',
    headerName: '#',
    width: 50,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
  },
  {
    field: 'orderType',
    headerName: '类型',
    width: 80,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    valueGetter: (value: OrderRow['orderType']) => orderTypeLabels[value] ?? value,
  },
  {
    field: 'user',
    headerName: '客户',
    width: 180,
    sortable: false,
    headerAlign: 'center',
    renderCell: ({ row }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar src={row.user.avatar ?? undefined} sx={{ width: 28, height: 28 }} />
        <div>
          <div>{row.user.name ?? '-'}</div>
          {row.user.mobile && (
            <div style={{ color: '#888', fontSize: '0.8em' }}>{row.user.mobile}</div>
          )}
        </div>
      </Box>
    ),
  },
  {
    field: 'detail',
    headerName: '详情',
    flex: 1,
    minWidth: 200,
    sortable: false,
    headerAlign: 'center',
    renderCell: ({ row }) => (
      <Box sx={{ whiteSpace: 'pre-line' }}>{formatDetail(row.detail)}</Box>
    ),
  },
  {
    field: 'createTime',
    headerName: '下单时间',
    width: 170,
    sortable: false,
    headerAlign: 'center',
    valueGetter: (value: string) => new Date(value).toLocaleString('zh-CN'),
  },
  {
    field: 'status',
    headerName: '状态',
    width: 100,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: ({ row }) => {
      const st = statusLabels[row.status]
      return <Chip label={st.label} color={st.color} size="small" />
    },
  },
  {
    field: 'amount',
    headerName: '金额',
    width: 100,
    sortable: false,
    align: 'right',
    headerAlign: 'center',
    valueGetter: (value: number) => `¥${value}`,
  },
]

export default function OrdersPage() {
  const { currentSite } = useSiteContext()
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(new Date())
  const [mobileInput, setMobileInput] = useState('')
  const [orderTypeFilter, setOrderTypeFilter] = useState<OrderType | ''>('')
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null)

  const [edges, setEdges] = useState<OrdersQuery['orders']['edges']>([])
  const [pageInfo, setPageInfo] = useState<OrdersQuery['orders']['pageInfo']>({
    __typename: 'PageInfo',
    hasNextPage: false,
    endCursor: null,
  })

  const [execute, { loading, error, fetchMore }] = useLazyQuery(OrdersDocument, {
    fetchPolicy: 'network-only',
  })

  const executeRef = useRef(execute)
  executeRef.current = execute

  const loadingMore = useRef(false)
  const lastFetchedCursor = useRef<string | null | undefined>(undefined)

  // Initial load when site is available
  const initialLoaded = useRef(false)
  useEffect(() => {
    if (currentSite && !initialLoaded.current) {
      initialLoaded.current = true
      executeRef
        .current({
          variables: {
            filter: {
              siteId: currentSite.id,
              endDate: endOfDay(new Date()).toISOString(),
              adminView: true,
            },
          },
        })
        .then((result) => {
          if (result.data) {
            setEdges(result.data.orders.edges)
            setPageInfo(result.data.orders.pageInfo)
          }
        })
    }
  }, [currentSite])

  const isValidMobile = (value: string): boolean => {
    return value === '' || /^1[3-9]\d{9}$/.test(value)
  }

  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 11)
    setMobileInput(value)
  }

  const handleSearch = () => {
    if (!isValidMobile(mobileInput)) return
    loadingMore.current = false
    lastFetchedCursor.current = undefined
    setEdges([])
    setPageInfo({ __typename: 'PageInfo', hasNextPage: false, endCursor: null })
    execute({
      variables: {
        filter: {
          siteId: currentSite?.id as string,
          startDate: startDate ? startOfDay(startDate).toISOString() : undefined,
          endDate: endDate ? endOfDay(endDate).toISOString() : undefined,
          mobile: mobileInput || undefined,
          orderType: orderTypeFilter || undefined,
          adminView: true,
        },
      },
    }).then((result) => {
      if (result.data) {
        setEdges(result.data.orders.edges)
        setPageInfo(result.data.orders.pageInfo)
      }
    })
  }

  const handleRowsScrollEnd = useCallback(() => {
    if (
      pageInfo.hasNextPage &&
      !loadingMore.current &&
      pageInfo.endCursor !== lastFetchedCursor.current
    ) {
      loadingMore.current = true
      lastFetchedCursor.current = pageInfo.endCursor
      fetchMore({
        variables: { after: pageInfo.endCursor },
      }).then((result) => {
        const more = result.data
        if (more) {
          setEdges((prev) => [...prev, ...more.orders.edges])
          setPageInfo(more.orders.pageInfo)
        }
        loadingMore.current = false
      })
    }
  }, [pageInfo, fetchMore])

  const rows = edges.map((e) => e.node)

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px - 48px)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h4">订单管理</Typography>
        <Box sx={{ flex: 1 }} />
        <DatePicker
          label="开始日期"
          value={startDate}
          onChange={setStartDate}
          format="yyyy-MM-dd"
          maxDate={endDate || undefined}
          slotProps={{
            textField: {
              size: 'small',
              sx: { width: 200 },
              slotProps: { inputLabel: { shrink: true } },
            },
            field: { clearable: true, onClear: () => setStartDate(null) },
          }}
        />
        <DatePicker
          label="结束日期"
          value={endDate}
          onChange={setEndDate}
          format="yyyy-MM-dd"
          minDate={startDate || undefined}
          maxDate={new Date()}
          slotProps={{
            textField: {
              size: 'small',
              sx: { width: 160 },
              slotProps: { inputLabel: { shrink: true } },
            },
          }}
        />
        <TextField
          select
          label="类型"
          size="small"
          value={orderTypeFilter}
          onChange={(e) => setOrderTypeFilter(e.target.value as OrderType | '')}
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              endAdornment: orderTypeFilter ? (
                <IconButton
                  size="small"
                  onClick={() => setOrderTypeFilter('')}
                  sx={{ mr: 1 }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              ) : undefined,
            },
          }}
          sx={{ width: 150 }}
        >
          {Object.entries(orderTypeLabels).map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="手机号"
          size="small"
          value={mobileInput}
          onChange={handleMobileChange}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: {
              inputMode: 'numeric',
              pattern: '[0-9]*',
              maxLength: 11,
            },
          }}
          sx={{ width: 160 }}
        />
        <Button
          variant="contained"
          size="small"
          onClick={handleSearch}
          disabled={!isValidMobile(mobileInput)}
        >
          搜索
        </Button>
      </Box>

      {error && <Alert severity="error">加载订单失败: {error.message}</Alert>}

      {initialLoaded.current && (
        <DataGridPro
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          hideFooter
          disableRowSelectionOnClick
          disableColumnMenu
          density="compact"
          getRowHeight={() => 'auto'}
          onRowsScrollEnd={handleRowsScrollEnd}
          onRowClick={(params) => setSelectedOrderId(params.row.id)}
          localeText={{ noRowsLabel: '暂无订单' }}
          sx={{
            flex: 1,
            minHeight: 0,
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
              py: 1,
            },
          }}
        />
      )}

      <OrderDialog orderId={selectedOrderId} onClose={() => setSelectedOrderId(null)} />
    </Box>
  )
}
